import React, { useState } from 'react';
import { useMyProvider } from '../../hooks/useMyProvider';
import { useSentimentTrends } from '../../hooks/useSentimentTrends';
import { analyticsService } from '../../services/analytics';
import { SentimentSummary } from '../../types/analytics';
import SentimentLineChart from '../../components/Charts/SentimentLineChart';
import AspectBarChart from '../../components/Charts/AspectBarChart';
import MetricCard from '../../components/Dashboard/MetricCard';
import NotAvailable from '../../components/Common/NotAvailable';

const Sentiment: React.FC = () => {
  const { provider, loading: providerLoading, error: providerError } = useMyProvider();
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<SentimentSummary | null>(null);
  // Starts true so the cards don't flash "Not available" before the first fetch
  const [loadingSummary, setLoadingSummary] = useState(true);

  const { data: trendsData, loading: trendsLoading } = useSentimentTrends(
    provider?.id || null,
    period,
    days
  );

  React.useEffect(() => {
    if (provider?.id) {
      setLoadingSummary(true);
      analyticsService
        .getSentimentSummary(provider.id)
        .then(setSummary)
        .catch(console.error)
        .finally(() => setLoadingSummary(false));
    }
  }, [provider?.id]);

  if (providerLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          {providerError || 'No provider profile found. Create your provider profile to see analytics.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-['Ovo'] text-2xl md:text-3xl text-[#1f1f1f] mb-2">
          Sentiment Analysis
        </h1>
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          Comprehensive sentiment breakdown across all reviews
        </p>
      </div>

      {/* Sentiment Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Positive"
          value={
            summary ? (
              `${summary.sentiment_distribution.positive_percentage.toFixed(1)}%`
            ) : (
              <NotAvailable className="text-base" />
            )
          }
          subtitle={
            summary ? `${summary.sentiment_distribution.positive_count} reviews` : undefined
          }
          color="bg-green-50"
          loading={loadingSummary}
          icon={
            <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <MetricCard
          title="Neutral"
          value={
            summary ? (
              `${summary.sentiment_distribution.neutral_percentage.toFixed(1)}%`
            ) : (
              <NotAvailable className="text-base" />
            )
          }
          subtitle={summary ? `${summary.sentiment_distribution.neutral_count} reviews` : undefined}
          color="bg-yellow-50"
          loading={loadingSummary}
          icon={
            <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7 5a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <MetricCard
          title="Negative"
          value={
            summary ? (
              `${summary.sentiment_distribution.negative_percentage.toFixed(1)}%`
            ) : (
              <NotAvailable className="text-base" />
            )
          }
          subtitle={
            summary ? `${summary.sentiment_distribution.negative_count} reviews` : undefined
          }
          color="bg-red-50"
          loading={loadingSummary}
          icon={
            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7 5a1 1 0 011 1 3 3 0 006 0 1 1 0 112 0 5 5 0 01-10 0 1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-4 sm:mb-0">Sentiment Trends</h2>

          {/* Period Selector */}
          <div className="flex items-center space-x-4">
            <div className="flex space-x-2">
              {(['daily', 'weekly', 'monthly'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs font-['Open_Sans'] font-medium uppercase rounded transition-colors ${
                    period === p
                      ? 'bg-[#1f1f1f] text-white'
                      : 'bg-[#f8f6f2] text-[#1f1f1f] hover:bg-[#eaeaea]'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Days Selector */}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-1 text-xs font-['Open_Sans'] font-medium bg-white border border-[#eaeaea] rounded focus:outline-none focus:border-[#c6a664]"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>

        {trendsLoading ? (
          <div className="flex items-center justify-center h-80">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
          </div>
        ) : trendsData && trendsData.data_points.length > 0 ? (
          <SentimentLineChart data={trendsData.data_points} height={400} showMovingAverage />
        ) : (
          <div className="flex items-center justify-center h-80 text-[#767472]">
            <p className="font-['Open_Sans'] text-sm">No trend data available</p>
          </div>
        )}
      </div>

      {/* Platform Comparison */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-6">Platform Comparison</h2>

        {loadingSummary ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
          </div>
        ) : summary && summary.platform_breakdown.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {summary.platform_breakdown.map((data) => (
              <div key={data.platform} className="border border-[#eaeaea] rounded-lg p-4">
                <h3 className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f] uppercase mb-3">
                  {data.platform}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-['Open_Sans'] text-[#767472]">Total:</span>
                    <span className="font-['Open_Sans'] font-semibold text-[#1f1f1f]">
                      {data.review_count}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-['Open_Sans'] text-[#767472]">Avg Score:</span>
                    <span className="font-['Open_Sans'] font-semibold text-[#1f1f1f]">
                      {data.avg_sentiment_score.toFixed(3)}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-[#eaeaea]">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-green-600">Positive:</span>
                      <span className="font-semibold text-green-600">{data.positive_count}</span>
                    </div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-yellow-600">Neutral:</span>
                      <span className="font-semibold text-yellow-600">{data.neutral_count}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600">Negative:</span>
                      <span className="font-semibold text-red-600">{data.negative_count}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-[#767472]">
            <p className="font-['Open_Sans'] text-sm">No platform data available</p>
          </div>
        )}
      </div>

      {/* Aspect Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-6">Sentiment by Aspect</h2>

        {loadingSummary ? (
          <div className="flex items-center justify-center h-80">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
          </div>
        ) : summary && summary.aspect_breakdown.length > 0 ? (
          <AspectBarChart data={summary.aspect_breakdown} height={400} />
        ) : (
          <div className="flex items-center justify-center h-80 text-[#767472]">
            <p className="font-['Open_Sans'] text-sm">No aspect data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sentiment;
